import React, { ReactNode, useEffect } from 'react';
import classNames from 'classnames';
import { useVirtualizer } from '@tanstack/react-virtual';

import './VirtualArrayRender.styles.scss';

export type VirtualArrayRenderProps<T> = {
  className?: string;
  items: T[];
  estimateSize: number;
  overscan?: number;
  getScrollElement: () => Element;
  renderItem: (item: T, index: number) => ReactNode;
  getItemKey?: (item: T, index: number) => string;
};

const VirtualArrayRender = <T,>(props: VirtualArrayRenderProps<T>) => {
  const {
    className,
    items,
    estimateSize,
    overscan = 5,
    getScrollElement,
    renderItem,
    getItemKey,
  } = props;

  const classes = classNames('virtual-array-render', className);

  const rowVirtualizer = useVirtualizer({
    count: items.length,
    overscan,
    getScrollElement,
    estimateSize: () => estimateSize,
    getItemKey: (index) => getItemKey(items[index], index),
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    rowVirtualizer.measure();
  }, [rowVirtualizer, items.length]);

  return (
    <div
      className={classes}
      style={{
        height: rowVirtualizer.getTotalSize(),
      }}
    >
      {virtualItems.map((virtualItem) => {
        const item = items[virtualItem.index];

        return (
          <div
            key={String(virtualItem.key)}
            ref={rowVirtualizer.measureElement}
            data-index={virtualItem.index}
            className="virtual-array-render__row"
            style={{
              height: `${virtualItem.size}px`,
              transform: `translateY(${virtualItem.start}px)`,
            }}
          >
            {renderItem(item, virtualItem.index)}
          </div>
        );
      })}
    </div>
  );
};

export default VirtualArrayRender;
